import { z } from 'zod';
import {
  AuditSchema,
  InstantSchema,
  InvoiceIdSchema,
  MoneySchema,
  OrderIdSchema,
  PartyIdSchema,
  PaymentIdSchema,
  PlainDateSchema,
  QuantitySchema,
  TenantScopedSchema,
  addMoney,
  zeroMoney,
} from './primitives.js';

/* ------------------------------------------------------------------ *
 * Invoices — both directions.
 *
 * The brief asks for sales *and* purchase invoices. They are the same
 * document with the counterparty on the other side, so they are one
 * type with a `direction`. That means one list screen, one PDF
 * pipeline, one ageing report — and "what do I owe / what am I owed"
 * becomes a filter instead of a second codebase.
 * ------------------------------------------------------------------ */

export const InvoiceDirectionSchema = z.enum(['sales', 'purchase']);
export type InvoiceDirection = z.infer<typeof InvoiceDirectionSchema>;

export const InvoiceStatusSchema = z.enum([
  'draft',
  'issued',
  'sent',
  'part-paid',
  'paid',
  'overdue',
  'disputed',
  'void',
]);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

export const InvoiceLineSchema = z.object({
  id: z.string().min(1),
  productId: z.string().nullable().default(null),
  description: z.string().min(1),
  quantity: QuantitySchema,
  unitPrice: MoneySchema,
  discountBps: z.number().int().min(0).max(10_000).default(0),
  taxRateKey: z.string().min(1),
  taxBps: z.number().int().min(0).max(10_000),
  /** Frozen at issue. A tax rate change next year must not rewrite last year's books. */
  net: MoneySchema,
  tax: MoneySchema,
  surcharge: MoneySchema.nullable().default(null),
});
export type InvoiceLine = z.infer<typeof InvoiceLineSchema>;

export const InvoiceSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: InvoiceIdSchema,
  direction: InvoiceDirectionSchema,
  /** Series + sequence. Legal numbering is per series and must never have gaps. */
  series: z.string().min(1),
  number: z.string().min(1),
  status: InvoiceStatusSchema,

  partyId: PartyIdSchema,
  partyName: z.string().min(1),
  partyTaxId: z.string().nullable().default(null),
  /** Address frozen at issue — the customer may move next month. */
  billingAddress: z.string().min(1),

  issueDate: PlainDateSchema,
  dueDate: PlainDateSchema,
  currency: z.string().length(3),
  /** For foreign-currency documents; 1.0 when the invoice is in the business currency. */
  exchangeRate: z.number().positive().default(1),

  orderIds: z.array(OrderIdSchema).default([]),
  lines: z.array(InvoiceLineSchema),

  net: MoneySchema,
  tax: MoneySchema,
  surcharge: MoneySchema.nullable().default(null),
  gross: MoneySchema,
  /** Sum of allocated payments. Kept denormalised: the debt list reads it on every row. */
  paid: MoneySchema,

  pdfUrl: z.string().nullable().default(null),
  sentAt: InstantSchema.nullable().default(null),
  note: z.string().nullable().default(null),
  /** Credit note pointer, when this document reverses another. */
  correctsInvoiceId: InvoiceIdSchema.nullable().default(null),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

export const invoiceOutstanding = (invoice: Invoice): import('./primitives.js').Money => ({
  minor: invoice.gross.minor - invoice.paid.minor,
  currency: invoice.currency,
});

export const daysOverdue = (invoice: Invoice, today: string): number => {
  if (invoice.status === 'paid' || invoice.status === 'void') return 0;
  const due = Date.parse(`${invoice.dueDate}T00:00:00Z`);
  const now = Date.parse(`${today}T00:00:00Z`);
  return Math.max(0, Math.floor((now - due) / 86_400_000));
};

/** Standard ageing buckets. The debt screen groups by these. */
export const AGEING_BUCKETS = [
  { key: 'current', from: -Infinity, to: 0 },
  { key: 'd1_30', from: 1, to: 30 },
  { key: 'd31_60', from: 31, to: 60 },
  { key: 'd61_90', from: 61, to: 90 },
  { key: 'd90plus', from: 91, to: Infinity },
] as const;
export type AgeingBucketKey = (typeof AGEING_BUCKETS)[number]['key'];

export const ageingBucket = (days: number): AgeingBucketKey =>
  AGEING_BUCKETS.find((b) => days >= b.from && days <= b.to)?.key ?? 'current';

/* ------------------------------------------------------------------ *
 * Payments
 *
 * A payment is money moving. It is allocated across invoices, and the
 * allocation is a list because a restaurant pays three invoices with
 * one transfer and a part of a fourth. Modelling payment→invoice as
 * one-to-one is the bug you discover during the first reconciliation.
 * ------------------------------------------------------------------ */

export const PaymentAllocationSchema = z.object({
  invoiceId: InvoiceIdSchema,
  invoiceNumber: z.string().min(1),
  amount: MoneySchema,
});
export type PaymentAllocation = z.infer<typeof PaymentAllocationSchema>;

export const PaymentSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: PaymentIdSchema,
  /** 'in' = a customer paid us. 'out' = we paid a supplier. */
  direction: z.enum(['in', 'out']),
  partyId: PartyIdSchema,
  partyName: z.string().min(1),
  date: PlainDateSchema,
  amount: MoneySchema,
  method: z.enum(['cash', 'transfer', 'card', 'direct-debit', 'offset', 'other']),
  reference: z.string().nullable().default(null),
  allocations: z.array(PaymentAllocationSchema).default([]),
  status: z.enum(['pending', 'cleared', 'bounced', 'void']).default('cleared'),
  note: z.string().nullable().default(null),
});
export type Payment = z.infer<typeof PaymentSchema>;

export const unallocatedAmount = (payment: Payment): import('./primitives.js').Money => {
  const allocated = payment.allocations.reduce<import('./primitives.js').Money>(
    (acc, a) => addMoney(acc, a.amount),
    zeroMoney(payment.amount.currency),
  );
  return { minor: payment.amount.minor - allocated.minor, currency: payment.amount.currency };
};

/** One row of the debt screen, in either direction. */
export interface PartyBalance {
  partyId: string;
  partyName: string;
  direction: 'receivable' | 'payable';
  outstanding: import('./primitives.js').Money;
  overdue: import('./primitives.js').Money;
  oldestDays: number;
  creditLimitMinor: number | null;
  invoiceCount: number;
  buckets: Record<AgeingBucketKey, number>;
}
